import React from 'react';

import Debug from '@/components/Debug';
import Accordion from '@/ui/components/Accordion';
import CopyContent from '@/ui/components/CopyContent';
import DataList, { DataRow } from '@/ui/components/DataList';
import Heading from '@/ui/components/Heading';
import { currencyInCentsFormatter } from '@/utils/currencies';

import { Props } from './types';

const ExternalPolicyVerification = ({
  externalPolicyVerification,
  debugObject,
  isDiscovered = false
}: Props) => {
  const {
    source,
    effective_on,
    terminate_on,
    external_name,
    plan_type_description,
    eligibility,
    finance,
    external_vendor_id,
    service_code,
    group_name,
    coverages
  } = externalPolicyVerification;

  const coPay =
    finance?.office_copay_in_cents != null
      ? currencyInCentsFormatter(finance.office_copay_in_cents)
      : '-';

  const coInsurance =
    finance?.coinsurance_percentage != null ? `${finance.coinsurance_percentage}%` : '-';

  return (
    <DataList className="relative [&>div]:flex [&>div]:flex-row [&>div]:gap-2">
      <DataRow label="Source:">{source ?? '-'}</DataRow>
      {!isDiscovered && (
        <DataRow label="Response:">{eligibility?.insurance_verification_status ?? '-'}</DataRow>
      )}
      <DataRow label="Eligibility status:">
        {`${eligibility?.eligibility_status_code} - ${eligibility?.eligibility_status_description}`}
      </DataRow>
      <DataRow label="Plan:">{external_name}</DataRow>
      <DataRow label="Plan description:">{plan_type_description ?? '-'}</DataRow>
      <DataRow label="Plan start date:">{effective_on ?? '-'}</DataRow>
      <DataRow label="Plan end date:">{terminate_on ?? '-'}</DataRow>
      <DataRow label="Co-pay:">{coPay}</DataRow>
      <DataRow label="Co-insurance:">{coInsurance}</DataRow>
      <DataRow label="Deductible remaining:">
        {finance?.deductible_remaining_in_cents || finance?.deductible_remaining_in_cents === 0
          ? currencyInCentsFormatter(finance?.deductible_remaining_in_cents)
          : '-'}
      </DataRow>
      <DataRow label="OOP max remaining:">
        {finance?.out_of_pocket_remaining_in_cents ||
        finance?.out_of_pocket_remaining_in_cents === 0
          ? currencyInCentsFormatter(finance?.out_of_pocket_remaining_in_cents)
          : '-'}
      </DataRow>
      <DataRow label="Service Code:">{service_code ?? '-'}</DataRow>
      <Heading className="mt-4" value={4} text="Additional info from Vendor" />

      <hr />
      <DataRow label="Group name:" sensitive>
        {group_name ?? '-'}
      </DataRow>
      <DataRow label="Trading partner ID:">
        {external_vendor_id ? <CopyContent content={external_vendor_id} /> : '-'}
      </DataRow>

      {coverages && coverages.length > 0 && (
        <Accordion buttonText={`Additional coverages (${coverages?.length})`}>
          <div className="divide-y divide-gray-200 space-y-4">
            {coverages?.map(
              ({ services, message, insurance_type, plan_name, plan_description }, idx) => (
                <div key={idx}>
                  <DataRow label="Plan name:">{plan_name || '-'}</DataRow>
                  <DataRow label="Plan description:">{plan_description ?? '-'}</DataRow>
                  <DataRow label="Services:">{services ? services.join(', ') : '-'}</DataRow>
                  <DataRow label="Message:">{message ?? '-'}</DataRow>
                  <DataRow label="Insurance type:">{insurance_type ?? '-'}</DataRow>
                </div>
              )
            )}
          </div>
        </Accordion>
      )}
      <div className="mb-6">
        <Debug object={debugObject ?? externalPolicyVerification} />
      </div>
    </DataList>
  );
};
export default React.memo(ExternalPolicyVerification);
